import React from "react";
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import moment from "moment";

import API from "@beardeddevops/react.api";
import Framework from "@beardeddevops/react.framework";
import Types from "constants/Types";
import {
	coveredMemberOptionIsDisabled,
	coveredMemberPkey,
	DEFAULT_COVERED_MEMBER_LIMIT,
	membershipCountsTowardCustomerLimit,
	membershipIncludesCustomer,
	membershipMatchesPayingCustomer,
	normalizeCoveredMemberPkeys,
} from "utils/membershipCoveredMembers";

const toastOptions = {
	position: API.Toast.POSITION && API.Toast.POSITION.TOP_CENTER ? API.Toast.POSITION.TOP_CENTER : "top-center",
};

const normalizeFrameworkDate = (value) => {
	if (!value) {
		return "";
	}

	const date = String(value).slice(0, 10);
	return moment(date, "YYYY-MM-DD", true).isValid() ? date : "";
};

const formatCurrency = (value) => {
	const amount = parseFloat(value);
	return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "$0.00";
};

const normalizeBillingPeriod = (membership) => (
	membership && membership.billing_period === "yearly" ? "yearly" : "monthly"
);

const billingPeriodUnitLabel = (billingPeriod) => (
	billingPeriod === "yearly" ? "year" : "month"
);

const formatBillingAmount = (membership) => (
	`${formatCurrency(membership.cost_per_month)} / ${billingPeriodUnitLabel(normalizeBillingPeriod(membership))}`
);

const isFreeMembership = (membership) => parseFloat(membership && membership.cost_per_month) === 0;

const parseInventoryGroupDiscounts = (value) => {
	if (Array.isArray(value)) {
		return value;
	}
	if (!value) {
		return [];
	}
	try {
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? parsed : [];
	} catch (error) {
		return [];
	}
};

const formatInventoryGroupDiscounts = (membership) => {
	const discounts = parseInventoryGroupDiscounts(membership.inventory_group_discounts);
	if (!discounts.length) {
		const fallbackDiscount = parseFloat(membership.discount_percentage || 0);
		return fallbackDiscount ? `${fallbackDiscount.toFixed(2)}%` : "No group discounts";
	}

	return discounts.map((discount) => {
		const groupName = discount.inventory_group_name
			|| (discount.sku_classes && discount.sku_classes.name)
			|| (discount.applies_to_all ? "All Items" : null)
			|| "Inventory Group";
		const discountAmount = parseFloat(discount.discount_amount || 0);
		if (Number.isFinite(discountAmount) && discountAmount > 0) {
			return `${groupName}: ${formatCurrency(discountAmount)}`;
		}
		return `${groupName}: ${parseFloat(discount.discount_percentage || 0).toFixed(2)}%`;
	}).join(", ");
};

const hasCardToken = (membership) => !!(membership.card_last_four || membership.tokenized_at);

const canReuseMembershipCard = (membership) => {
	const status = String(membership && membership.status || "").trim().toLowerCase();
	const terminal = parseInt(membership && membership.terminal, 10);

	return hasCardToken(membership)
		&& Number.isFinite(terminal)
		&& terminal > 0
		&& !membership.cancelled_at
		&& ["paused", "canceled", "cancelled", "deleted"].indexOf(status) === -1;
};

const formatCardLabel = (membership) => hasCardToken(membership)
	? `${membership.card_brand || "Card"} ${membership.card_last_four ? `**** ${membership.card_last_four}` : ""}`
	: "No card";

const firstTerminalPkey = (response) => {
	const terminals = Array.isArray(response) ? response : (response && Array.isArray(response.items) ? response.items : []);
	const firstTerminal = terminals[0];

	return firstTerminal && firstTerminal.pkey ? firstTerminal.pkey : "";
};

export default class MembershipEnrollmentModal extends Framework.Components.Object {
	constructor(props) {
		super(props, Types.Memberships._name);
		this.state = {
			...this.state,
			selectedPlan: "",
			customer: props.customer || "",
			coveredMembers: [],
			terminal: "",
			endDate: "",
			customerMemberships: Array.isArray(props.customerMemberships) ? props.customerMemberships : [],
			customerMembershipsLoading: false,
			terminalLoading: false,
			processing: false,
			processingAction: "",
			paymentReference: "",
			processingTerminal: "",
			abortProcessing: false,
			abortRequested: false,
		};
	}

	componentDidMount() {
		if (this.props.isOpen) {
			this.resetEnrollmentForm();
		}
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		if (typeof super.componentDidUpdate === 'function') {
			super.componentDidUpdate(prevProps, prevState, snapshot);
		}
		if (this.props.isOpen && !prevProps.isOpen) {
			this.resetEnrollmentForm();
		}
		if (this.props.customer !== prevProps.customer) {
			this.setState({
				customer: this.props.customer || "",
				coveredMembers: [],
			}, () => this.loadCustomerMemberships(this.props.customer || ""));
		}
	}

	resetEnrollmentForm = () => {
		const plans = this.props.plans || [];
		const selectedPlan = this.props.plan
			? this.props.plan.pkey
			: (plans[0] ? plans[0].pkey : "");
		this.setState({
			selectedPlan,
			customer: this.props.customer || "",
			coveredMembers: [],
			terminal: "",
			endDate: "",
			customerMemberships: Array.isArray(this.props.customerMemberships) ? this.props.customerMemberships : [],
			customerMembershipsLoading: false,
			terminalLoading: false,
			processing: false,
			processingAction: "",
			paymentReference: "",
			processingTerminal: "",
			abortProcessing: false,
			abortRequested: false,
		}, () => {
			this.loadDefaultTerminal(selectedPlan);
			this.loadCustomerMemberships(this.props.customer || "");
		});
	}

	findPlan = (planPkey) => {
		if (this.props.plan && parseInt(this.props.plan.pkey, 10) === parseInt(planPkey, 10)) {
			return this.props.plan;
		}

		return (this.props.plans || []).find((plan) => parseInt(plan.pkey, 10) === parseInt(planPkey, 10));
	};

	updateSelect = (name, value) => {
		const nextState = {[name]: value || ""};
		if (name === "customer") {
			nextState.coveredMembers = normalizeCoveredMemberPkeys(this.state.coveredMembers, value);
		}
		this.setState(nextState, () => {
			if (name === "customer") {
				this.loadCustomerMemberships(value);
			}
		});
	};

	loadCustomerMemberships = async (customer) => {
		const customerPkey = coveredMemberPkey(customer);
		const requestId = (this.customerMembershipRequestId || 0) + 1;
		this.customerMembershipRequestId = requestId;
		if (customerPkey === null) {
			this.setState({customerMemberships: [], customerMembershipsLoading: false});
			return;
		}

		this.setState({customerMembershipsLoading: true});
		try {
			const requestType = "GET_CUSTOMER_MEMBERSHIPS";
			const requestModel = new API.BuildClass(Types.Memberships._name, requestType);
			requestModel.object = {
				customer_lookup: customerPkey,
				include_activity: false,
			};
			const response = await requestModel.submit(null, requestType, true);
			if (requestId === this.customerMembershipRequestId
				&& coveredMemberPkey(this.state.customer) === customerPkey
			) {
				this.setState({
					customerMemberships: Array.isArray(response && response.memberships) ? response.memberships : [],
					customerMembershipsLoading: false,
				});
			}
		} catch (error) {
			if (requestId === this.customerMembershipRequestId) {
				this.setState({customerMemberships: [], customerMembershipsLoading: false});
				API.Toast.error(error.message || "Unable to check this customer's memberships.", toastOptions);
			}
		}
	};

	updateCoveredMembers = (name, values) => {
		const coveredMemberLimit = parseInt(this.props.coveredMemberLimit, 10) || DEFAULT_COVERED_MEMBER_LIMIT;
		const coveredMembers = normalizeCoveredMemberPkeys(values, this.state.customer);

		if (coveredMembers.length > coveredMemberLimit) {
			API.Toast.error(`A membership can include no more than ${coveredMemberLimit} covered members.`, toastOptions);
			return;
		}

		this.setState({coveredMembers});
	};

	coveredMemberFilter = (coveredMember) => (
		coveredMemberPkey(coveredMember) !== coveredMemberPkey(this.state.customer)
	);

	coveredMemberOptionDisabled = (coveredMember) => coveredMemberOptionIsDisabled(
		this.state.coveredMembers,
		coveredMember,
		parseInt(this.props.coveredMemberLimit, 10) || DEFAULT_COVERED_MEMBER_LIMIT,
	);

	updatePlan = (property, selectedPlan) => {
		this.setState({
			selectedPlan,
			terminal: "",
		}, () => this.loadDefaultTerminal(selectedPlan));
	};

	updateEndDate = (property, value) => {
		this.setState({endDate: normalizeFrameworkDate(value)});
	};

	loadDefaultTerminal = async (selectedPlan = this.state.selectedPlan) => {
		const requestId = (this.terminalRequestId || 0) + 1;
		this.terminalRequestId = requestId;
		if (isFreeMembership(this.findPlan(selectedPlan))) {
			this.setState({terminalLoading: false});
			return;
		}
		this.setState({terminalLoading: true});
		try {
			const terminalsModel = new API.BuildClass(Types.Terminals_Altrupay._name, API.RequestType.READ_ALL);
			const response = await terminalsModel.submit(null, API.RequestType.READ_ALL);
			const terminal = firstTerminalPkey(response);
			if (requestId === this.terminalRequestId) {
				const selectedPlanStillCurrent = parseInt(this.state.selectedPlan, 10) === parseInt(selectedPlan, 10);
				this.setState({
					terminal: terminal
						&& this.props.isOpen
						&& selectedPlanStillCurrent
						&& !this.state.terminal
						? terminal
						: this.state.terminal,
					terminalLoading: false,
				});
			}
		} catch (error) {
			if (requestId === this.terminalRequestId) {
				this.setState({terminalLoading: false});
				API.Toast.error(error.message || "Unable to load the default terminal.", toastOptions);
			}
		}
	};

	getReusableCard = (customerPkey = this.state.customer) => {
		const customerId = coveredMemberPkey(customerPkey);
		if (customerId === null) {
			return null;
		}

		return (this.state.customerMemberships || [])
			.filter((membership) => membershipMatchesPayingCustomer(membership, customerId) && canReuseMembershipCard(membership))
			.sort((a, b) => {
				const aDate = a.tokenized_at ? moment(a.tokenized_at).valueOf() : 0;
				const bDate = b.tokenized_at ? moment(b.tokenized_at).valueOf() : 0;
				if (aDate !== bDate) {
					return bDate - aDate;
				}
				const aModified = a.modified ? moment(a.modified).valueOf() : 0;
				const bModified = b.modified ? moment(b.modified).valueOf() : 0;
				if (aModified !== bModified) {
					return bModified - aModified;
				}
				return (coveredMemberPkey(b.pkey) || 0) - (coveredMemberPkey(a.pkey) || 0);
			})[0] || null;
	};

	getExistingCustomerMembership = (customerPkey = this.state.customer) => {
		if (this.props.allowMultipleMemberships) {
			return null;
		}

		const customerId = coveredMemberPkey(customerPkey);
		if (customerId === null) {
			return null;
		}

		return (this.state.customerMemberships || [])
			.find((membership) => (
				membershipIncludesCustomer(membership, customerId)
				&& membershipCountsTowardCustomerLimit(membership, moment().format("YYYY-MM-DD"))
			)) || null;
	};

	createPaymentReference = () => {
		const randomSuffix = Math.random().toString(36).replace(/[^a-z0-9]/g, "").slice(2, 8).toUpperCase();
		return `M${Date.now().toString(36).toUpperCase()}${randomSuffix}`.slice(0, 20);
	};

	abortTerminalPayment = async () => {
		const {processingTerminal, paymentReference, abortProcessing} = this.state;
		if (abortProcessing || !processingTerminal || !paymentReference) {
			return;
		}

		this.setState({abortProcessing: true, abortRequested: true});
		try {
			const requestType = "POST_ABORT";
			const requestModel = new API.BuildClass(Types.Memberships._name, requestType);
			requestModel.object = {
				terminal: processingTerminal,
				payment_reference_id: paymentReference,
			};
			await requestModel.submit(null, requestType, true);
			API.Toast.info("Abort request sent. Waiting for the terminal request to stop.", toastOptions);
		} catch (error) {
			API.Toast.error(error.message || "Unable to abort the terminal request.", toastOptions);
			this.setState({abortRequested: false});
		} finally {
			this.setState({abortProcessing: false});
		}
	};

	submit = async (captureNewCard = false, billNow = false) => {
		if (this.state.processing) {
			return;
		}
		const {customer, coveredMembers, selectedPlan, terminal, endDate} = this.state;
		const plan = this.findPlan(selectedPlan);
		const freeMembership = isFreeMembership(plan);
		const normalizedEndDate = normalizeFrameworkDate(endDate);
		const effectiveTerminal = coveredMemberPkey(terminal)
			|| coveredMemberPkey(plan && plan.terminal)
			|| "";
		const coveredMemberLimit = parseInt(this.props.coveredMemberLimit, 10) || DEFAULT_COVERED_MEMBER_LIMIT;
		const reusableCard = freeMembership ? null : this.getReusableCard(customer);
		const shouldCaptureNewCard = !freeMembership && (captureNewCard || !reusableCard);
		const existingCustomerMembership = this.getExistingCustomerMembership(customer);

		if (!plan || !plan.pkey) {
			API.Toast.error("Select a membership plan first.", toastOptions);
			return;
		}
		if (!customer) {
			API.Toast.error("Select a customer before adding this membership.", toastOptions);
			return;
		}
		if (this.state.customerMembershipsLoading) {
			API.Toast.warning("Wait for this customer's memberships to finish loading.", toastOptions);
			return;
		}
		if (coveredMembers.length > coveredMemberLimit) {
			API.Toast.error(`A membership can include no more than ${coveredMemberLimit} covered members.`, toastOptions);
			return;
		}
		if (coveredMembers.length && this.props.coveredMembersReady !== true) {
			API.Toast.error("Covered-member storage must be installed before adding covered members.", toastOptions);
			return;
		}
		if (existingCustomerMembership) {
			API.Toast.error("This customer already has a membership. Turn on multiple memberships before adding another.", toastOptions);
			return;
		}
		if (shouldCaptureNewCard && !effectiveTerminal) {
			API.Toast.error("Select a terminal before capturing a card.", toastOptions);
			return;
		}
		if (shouldCaptureNewCard) {
			const terminalConfirmMessage = billNow
				? `Charge ${formatCurrency(plan.cost_per_month)} on the terminal now and save this card for future ${billingPeriodUnitLabel(normalizeBillingPeriod(plan))}ly membership billing?`
				: "Start a $0 card authorization on the terminal for this customer membership?";
			if (!window.confirm(terminalConfirmMessage)) {
				return;
			}
		}
		if (billNow && reusableCard && !window.confirm(`Create this membership and charge ${formatBillingAmount(plan)} now?`)) {
			return;
		}

		const usesTerminal = shouldCaptureNewCard;
		const paymentReference = usesTerminal ? this.createPaymentReference() : "";
		this.setState({
			processing: true,
			processingAction: billNow ? "bill" : (usesTerminal ? "capture" : "reuse"),
			paymentReference,
			processingTerminal: usesTerminal ? effectiveTerminal : "",
			abortProcessing: false,
			abortRequested: false,
		});
		try {
			const requestType = "POST_ENROLL";
			const requestModel = new API.BuildClass(Types.Memberships._name, requestType);
			requestModel.object = {
				membership_action: plan.pkey,
				customer,
				covered_member_ids: coveredMembers,
			};
			if (normalizedEndDate) {
				requestModel.object.end_date = normalizedEndDate;
			}
			if (!freeMembership && effectiveTerminal) {
				requestModel.object.terminal = effectiveTerminal;
			}
			if (shouldCaptureNewCard) {
				requestModel.object.capture_new_card = true;
			}
			if (!freeMembership && reusableCard && !shouldCaptureNewCard) {
				requestModel.object.reusable_membership = reusableCard.pkey;
			}
			if (!freeMembership && billNow) {
				requestModel.object.bill_now = true;
			}
			if (paymentReference) {
				requestModel.object.payment_reference_id = paymentReference;
			}

			const response = await requestModel.submit(null, requestType, true);
			if (response && response.is_free) {
				API.Toast.success(response.message || "Free customer membership created.", toastOptions);
			} else if (billNow && response && response.billing) {
				if (response.billing.success) {
					if (response.billing.card_token_saved === false) {
						API.Toast.warning(response.billing.message || "Customer membership created and billed, but the card token was not saved for future billing.", toastOptions);
					} else {
						API.Toast.success("Customer membership created and billed successfully.", toastOptions);
					}
				} else {
					API.Toast.warning(response.billing.message || "Customer membership created, but the billing attempt failed.", toastOptions);
				}
			} else {
				API.Toast.success(
					response && response.card_token_reused
						? "Customer membership created using the card on file."
						: "Customer membership created and card token saved.",
					toastOptions
				);
			}
			this.props.onComplete();
		} catch (error) {
			if (this.state.abortRequested) {
				API.Toast.info("Terminal request cancelled.", toastOptions);
			} else {
				API.Toast.error(error.message || "Unable to attach customer to this membership.", toastOptions);
			}
			this.setState({
				processing: false,
				processingAction: "",
				paymentReference: "",
				processingTerminal: "",
				abortProcessing: false,
				abortRequested: false,
			});
		}
	};

	renderPlanSummary = (fixedPlan, plan, plans, processing) => {
		if (fixedPlan) {
			return (
				<div className="membership-enrollment-plan-summary">
					<strong>{plan ? plan.name : "Select plan"}</strong>
					<small>{plan ? `${formatBillingAmount(plan)} - ${parseInt(plan.free_range_minutes, 10) || 0} range minutes` : ""}</small>
				</div>
			);
		}
		const planOptions = plans.map((membershipPlan) => ({
			...membershipPlan,
			membership_select_label: `${membershipPlan.name} - ${formatBillingAmount(membershipPlan)}`,
		}));

		return (
			<div className="membership-enrollment-plan-summary">
				<Framework.Elements.Select
					shouldLoad={true}
					disabled={processing}
					objects={planOptions}
					value={this.state.selectedPlan}
					error=""
					property={this.model.properties.membership_plan || {name: "membership_plan"}}
					label="Select Membership Plan"
					text="membership_select_label"
					update={this.updatePlan}
					nullable={true}
					default={{value: "", label: "Select Membership Plan"}}
					limit={0}
				/>
				<small>{plan ? `${parseInt(plan.free_range_minutes, 10) || 0} range minutes / ${formatInventoryGroupDiscounts(plan)}` : "Create a plan first if this list is empty."}</small>
			</div>
		);
	};

	render() {
		const {isOpen, onClose, plan: fixedPlan, plans = [], customer: fixedCustomer, customerName} = this.props;
		const {properties} = this.model;
		const {customer, coveredMembers, selectedPlan, terminal, endDate, processing, processingAction, paymentReference, processingTerminal, abortProcessing, abortRequested, terminalLoading} = this.state;
		const plan = this.findPlan(selectedPlan);
		const freeMembership = isFreeMembership(plan);
		const coveredMemberLimit = parseInt(this.props.coveredMemberLimit, 10) || DEFAULT_COVERED_MEMBER_LIMIT;
		const coveredMembersReady = this.props.coveredMembersReady === true;
		const reusableCard = freeMembership ? null : this.getReusableCard(customer);
		const existingCustomerMembership = this.getExistingCustomerMembership(customer);
		const membershipCheckPending = this.state.customerMembershipsLoading;
		const cardButtonLabel = reusableCard ? "Use Card on File" : "Capture Card";
		const processingLabel = processingAction === "bill" ? "Billing" : (processingAction === "capture" ? "Waiting for Card" : "Saving");
		const canAbortTerminalPayment = processing && !!processingTerminal && !!paymentReference && (processingAction === "bill" || processingAction === "capture");

		return (
			<Modal isOpen={isOpen} toggle={processing ? undefined : onClose} centered={true} size="lg" className="membership-enrollment-modal">
				<ModalHeader toggle={processing ? undefined : onClose}>
					Add Customer Membership
				</ModalHeader>
				<ModalBody>
					<div className="membership-enrollment-plan">
						<span>Membership Plan</span>
						{this.renderPlanSummary(fixedPlan, plan, plans, processing)}
					</div>
					<div className="membership-enrollment-fields">
						{fixedCustomer ?
							<div className="membership-fixed-customer">
								<span>Paying Member / Billing Contact</span>
								<strong>{customerName || `Customer #${fixedCustomer}`}</strong>
							</div>
							:
							<Framework.Elements.Select
								shouldLoad={isOpen}
								value={customer}
								error=""
								property={properties.customer || {name: "customer"}}
								label="Paying Member / Billing Contact"
								update={this.updateSelect}
								disabled={processing}
								textHelper="phone/email"
								nullable={true}
								limit={20}
							/>}
							<div className="membership-covered-member-picker">
								<Framework.Elements.Select
									key={`covered-members-${customer || "none"}-${coveredMembersReady ? "ready" : "disabled"}`}
									shouldLoad={isOpen && coveredMembersReady}
									values={coveredMembers}
									error=""
									property={properties.covered_members || {name: "covered_members"}}
									label="Covered Members"
									update={this.updateCoveredMembers}
									textHelper="phone/email"
									limit={20}
									filter={this.coveredMemberFilter}
									isOptionDisabled={this.coveredMemberOptionDisabled}
									disabled={processing || !customer || !coveredMembersReady}
									nullable={true}
									isMulti
									isClearable
								/>
								<small>
									{coveredMembersReady
										? `${coveredMembers.length} of ${coveredMemberLimit} covered members. Each person must have a customer record and is never charged individually.`
										: "Covered-member storage is not installed yet."}
								</small>
							</div>
							{!freeMembership ?
								<Framework.Elements.Select
									shouldLoad={isOpen}
									object={plan && plan.altrupay_spin_processing ? plan.altrupay_spin_processing : null}
									value={terminal}
									error=""
									property={properties.terminal || {name: "terminal"}}
									label="Terminals"
									update={this.updateSelect}
									disabled={processing}
									text="nickname|tpn"
									nullable={true}
									limit={20}
								/>
								: null}
							<div className="form-group membership-enrollment-end-date">
								<Framework.Elements.Date
									label="End Date (Optional)"
									property={properties.end_date || {name: "end_date"}}
									value={endDate}
									error=""
									disabled={processing}
									update={this.updateEndDate}
									minDate={moment().format("YYYY-MM-DD")}
								/>
								<small>Leave blank for no expiration.</small>
							</div>
						</div>
					{!freeMembership && reusableCard ?
						<div className="membership-card-on-file">
							<span>Card on File</span>
							<strong>{formatCardLabel(reusableCard)}</strong>
						</div>
						: null}
					{existingCustomerMembership ?
						<div className="membership-enrollment-warning">
							<i className="fa fa-exclamation-triangle"/>&nbsp;&nbsp;This customer already has a membership.
						</div>
						: null}
					</ModalBody>
					<ModalFooter>
						{canAbortTerminalPayment ?
							<Button color="danger" outline disabled={abortProcessing || abortRequested} onClick={this.abortTerminalPayment}>
								<i className="fa fa-ban"/>&nbsp;&nbsp;{abortProcessing || abortRequested ? "Cancelling" : "Abort Terminal"}
							</Button>
							: null}
						<Button className="secondary-button" disabled={processing} onClick={onClose}>Cancel</Button>
						{freeMembership ?
							<Button color="success" disabled={processing || membershipCheckPending || !!existingCustomerMembership} onClick={() => this.submit(false)}>
								<i className="fa fa-check"/>&nbsp;&nbsp;{processing ? "Creating" : "Create Free Membership"}
							</Button>
							:
							<React.Fragment>
								{reusableCard ?
									<Button color="info" outline disabled={processing || membershipCheckPending || terminalLoading || !!existingCustomerMembership} onClick={() => this.submit(true)}>
										<i className="fa fa-credit-card"/>&nbsp;&nbsp;{processing && processingAction === "capture" ? "Waiting for Card" : "Change Card"}
									</Button>
									: null}
								<Button className="primary-button" disabled={processing || membershipCheckPending || terminalLoading || !!existingCustomerMembership} onClick={() => this.submit(false)}>
									<i className="fa fa-credit-card"/>&nbsp;&nbsp;{processing ? processingLabel : cardButtonLabel}
								</Button>
								<Button color="success" disabled={processing || membershipCheckPending || terminalLoading || !!existingCustomerMembership} onClick={() => this.submit(false, true)}>
									<i className="fa fa-usd"/>&nbsp;&nbsp;{processing && processingAction === "bill" ? "Billing" : "Create & Bill Now"}
								</Button>
							</React.Fragment>}
					</ModalFooter>
			</Modal>
		);
	}
}
